import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Spinner, Table } from 'reactstrap';
import { PageHeader } from './PageHeader';
import {
  useApiCatalogList,
  useApiCatalogUsage,
  useApiCatalogDetail,
} from '../pages/ApiCatalog/useApiCatalog';

const fmtEur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const fmtNum = new Intl.NumberFormat('de-DE');

/**
 * #115/#116 — Gemeinsame Admin-Seite für ein gemetertes API-Produkt aus dem
 * CORE-`api_catalog`. Datenquelle: dieselben Operator-Routen wie der
 * API-Katalog (`GET /v1/admin/api-catalog` + `/usage` + `/:id`). Rendert
 * einheitlich Status/Umsatz, Tarife und Endpoint-Doku — ohne pro Produkt einen
 * eigenen Backend-Proxy zu brauchen (die Backends liegen extern: Nominatim,
 * LibreTranslate, Ollama, trademark-api, nexus). Produktspezifische
 * Einstellungen bleiben im vollständigen Katalog-Editor (`/admin/apis/api-catalog`).
 *
 * @param {object} p
 * @param {string} p.slug     — api_catalog-Slug (geocode/translate/llm/trademark/platform).
 * @param {string} p.title
 * @param {string} p.subtitle
 * @param {string} p.backend  — Klartext, wo das Produkt real bedient wird.
 */
export function ApiProductPage({ slug, title, subtitle, backend }) {
  const listQ = useApiCatalogList();
  const usageQ = useApiCatalogUsage(30);

  const row = useMemo(
    () => (listQ.data?.catalog ?? []).find((c) => c.slug === slug) ?? null,
    [listQ.data, slug],
  );
  const usage = useMemo(
    () => (usageQ.data?.apis ?? []).find((a) => a.slug === slug) ?? {},
    [usageQ.data, slug],
  );
  const detailQ = useApiCatalogDetail(row?.id);
  const detail = detailQ.data?.catalog ?? null;
  const tiers = detail?.tiers ?? [];
  const endpoints = detail?.endpoints ?? [];
  const notSeeded = !listQ.isLoading && !listQ.isError && !row;

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle} />

      <Alert
        color={listQ.isError ? 'danger' : 'light'}
        className="d-flex align-items-center gap-2 small flex-wrap"
      >
        <strong>Status:</strong>
        {listQ.isLoading ? (
          <><Spinner size="sm" /> lädt …</>
        ) : listQ.isError ? (
          <Badge color="danger" pill>Katalog nicht erreichbar</Badge>
        ) : notSeeded ? (
          <Badge color="warning" pill>nicht im Katalog geseedet</Badge>
        ) : row.isPublic ? (
          <Badge color="success" pill>öffentlich</Badge>
        ) : (
          <Badge color="secondary" pill>versteckt</Badge>
        )}
        {backend && <span className="text-muted">· Backend: {backend}</span>}
        {row && (
          <span className="ms-auto text-muted">
            30&nbsp;Tage: {fmtNum.format(usage.requests || 0)} Requests · MRR{' '}
            {fmtEur.format((usage.mrrCents || 0) / 100)}
          </span>
        )}
      </Alert>

      {notSeeded && (
        <Alert color="secondary" className="small">
          Dieses Produkt ist im CORE-<code>api_catalog</code> noch nicht angelegt (Slug{' '}
          <code>{slug}</code>). Über den <Link to="/admin/apis/api-catalog">API-Katalog</Link>{' '}
          seeden — danach erscheinen hier Tarife, Endpoints und Umsatz.
        </Alert>
      )}

      {tiers.length > 0 && (
        <section className="mb-4">
          <h5>Tarife</h5>
          <Table size="sm" bordered responsive className="align-middle">
            <thead>
              <tr>
                <th>Tarif</th>
                <th className="text-end">Preis/Monat</th>
                <th className="text-end">Inkl. Requests</th>
                <th className="text-end">Overage /1.000</th>
                <th className="text-end">Rate</th>
                <th>Aktiv</th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((t) => (
                <tr key={t.id || t.sku || t.tier}>
                  <td>
                    <div className="fw-semibold">{t.name || t.tier}</div>
                    {t.sku && <code className="small text-muted">{t.sku}</code>}
                  </td>
                  <td className="text-end">{fmtEur.format((t.unitAmount || 0) / 100)}</td>
                  <td className="text-end">
                    {t.includedRequests != null ? fmtNum.format(t.includedRequests) : '—'}
                  </td>
                  <td className="text-end">
                    {t.overagePer1000Cents != null ? fmtEur.format(t.overagePer1000Cents / 100) : '—'}
                  </td>
                  <td className="text-end">{t.rateLimitRps ? `${t.rateLimitRps}/s` : '—'}</td>
                  <td>
                    {t.isActive !== false ? (
                      <Badge color="success" pill>aktiv</Badge>
                    ) : (
                      <Badge color="secondary" pill>inaktiv</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </section>
      )}

      {endpoints.length > 0 && (
        <section className="mb-4">
          <h5>Endpoints</h5>
          <Table size="sm" bordered responsive className="align-middle">
            <thead>
              <tr>
                <th style={{ width: 90 }}>Methode</th>
                <th>Pfad</th>
                <th>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              {endpoints.map((e) => (
                <tr key={e.id || `${e.method}-${e.path}`}>
                  <td><Badge color="info">{e.method}</Badge></td>
                  <td><code className="small">{e.path}</code></td>
                  <td className="small">{e.summary}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </section>
      )}

      <div className="small text-muted">
        Voller Katalog-Editor (Tarife, Endpoints, Sichtbarkeit, Preise):{' '}
        <Link to="/admin/apis/api-catalog">API-Katalog</Link>.
        {detail?.docsUrl && (
          <> · <a href={detail.docsUrl} target="_blank" rel="noreferrer">Öffentliche Doku ↗</a></>
        )}
      </div>
    </div>
  );
}
